import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Crown, Check, Zap, GraduationCap, Trophy, Store } from "lucide-react";
import CoachSignupForm from "@/components/CoachSignupForm";
import TdSignupForm from "@/components/TdSignupForm";
import MerchantSignupForm from "@/components/MerchantSignupForm";
import { TierIcon } from "@/components/tierIcons";
export default function Auth() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(["register", "coach", "td"].includes(params.get("mode")) ? params.get("mode") : "login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [referralCode, setReferralCode] = useState(params.get("ref") || "");
  const [extRatings, setExtRatings] = useState({ dupr: "", vair: "" });
  const [manualDupr, setManualDupr] = useState(false);
  useEffect(() => { api.get("/dupr/public-config").then(({ data }) => setManualDupr(!!data.manual_dupr_allowed)).catch(() => {}); }, []);
  const [plan, setPlan] = useState("free");
  const [tiers, setTiers] = useState([]);
  useEffect(() => {
    api.get("/membership/tiers").then(({ data }) => setTiers(data)).catch(() => {});
  }, []);
  const selectedTier = tiers.find((t) => t.id === plan);
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login"
        ? { email: form.email, password: form.password }
        : {
            ...form,
            ...(extRatings.vair ? { vair_score: Number(extRatings.vair) } : {}),
            ...(manualDupr && extRatings.dupr ? { dupr_score: Number(extRatings.dupr) } : {}),
            ...(referralCode.trim() ? { referral_code: referralCode.trim() } : {}),
          };
      const { data } = await api.post(path, payload);
      await login(data.token, data.user);
      if (mode === "register" && selectedTier && selectedTier.price > 0) {
        toast.success(`Account created — redirecting to ${selectedTier.name} checkout…`);
        const { data: co } = await api.post("/payments/checkout", { package_id: plan, origin_url: window.location.origin });
        window.location.href = co.url;
        return;
      }
      toast.success(mode === "login" ? "Welcome back to the Bullpen" : "Account created — start drilling");
      const next = params.get("next");
      navigate(next && next.startsWith("/") ? next : "/profile");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-pb-base">
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden border-r border-pb-border">
        <img src="https://images.unsplash.com/photo-1781520422135-338da10e72e7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwzfHxzcG9ydHMlMjBhZ2lsaXR5JTIwdHJhaW5pbmd8ZW58MHx8fHwxNzgyMjU1NTI1fDA&ixlib=rb-4.1.0&q=85"
          alt="training" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-t from-pb-base via-pb-base/60 to-transparent" />
        <div className="relative flex items-center justify-center flex-1">
          <img src="/brand/pb-logo.png"
            alt="Pickle Bullpen" className="w-80 h-auto object-contain drop-shadow-[0_0_40px_rgba(163,230,53,0.25)]" />
        </div>
        <div className="relative">
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-pb-cyan mb-3">Performance Operating System</div>
          <h2 className="font-display font-black uppercase text-5xl leading-none mb-4">Every rep is<br/><span className="text-pb-red">measurable progression.</span></h2>
          <p className="text-zinc-400 max-w-md">LDA builds the player. BPL tests them under pressure. AI evolves both in real time. This is not casual recreation — it's a development pipeline.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-md animate-fade-up">
          <div className="lg:hidden flex items-center gap-2 mb-8 justify-center">
            <img src="/brand/pb-logo.png"
              alt="Pickle Bullpen" className="h-20 w-auto object-contain" />
          </div>
          <div className="flex border border-pb-border mb-8">
            {[["login", "Sign In", "#A3E635"], ["register", "Join", "#22D3EE"], ["coach", "Coaches", "#FACC15"], ["td", "Directors", "#E879F9"], ["merchant", "Merchants", "#FB923C"]].map(([m, l, col]) => (
              <button key={m} data-testid={`auth-tab-${m}`} onClick={() => setMode(m)}
                style={mode === m ? { backgroundColor: col, color: "#000" } : { color: col, boxShadow: `inset 0 -2px 0 ${col}55` }}
                className={`flex-1 py-3 text-[11px] font-bold uppercase tracking-widest transition-colors ${mode === m ? "" : "opacity-80 hover:opacity-100 hover:bg-white/5"}`}>
                {l}
              </button>
            ))}
          </div>
          {mode === "merchant" ? (
            <div data-testid="auth-merchant-panel">
              <div className="flex items-center gap-2 mb-2">
                <Store className="w-5 h-5 text-pb-cyan" />
                <span className="font-display font-black uppercase text-xl">Merchant Sign-Up</span>
              </div>
              <p className="text-sm text-zinc-400 mb-5">
                Sell goods, services or sports cards through the Bullpen and receive settlement payouts for your earned
                coins. Create your account, tell us about your business, agree to the Merchant Agreement, and we'll review
                your application.
              </p>
              <MerchantSignupForm />
            </div>
          ) : mode === "td" ? (
            <div data-testid="auth-td-panel">
              <div className="flex items-center gap-2 mb-2">
                <Trophy className="w-5 h-5 text-pb-cyan" />
                <span className="font-display font-black uppercase text-xl">Tournament Director Signup</span>
              </div>
              <p className="text-sm text-zinc-400 mb-5">Create your director login. Once we approve you, you'll get access to the Tournament Hub to create events, run brackets and enter scores.</p>
              <TdSignupForm />
            </div>
          ) : mode === "coach" ? (
            <div data-testid="auth-coach-panel">
              <div className="flex items-center gap-2 mb-2">
                <GraduationCap className="w-5 h-5 text-pb-cyan" />
                <span className="font-display font-black uppercase text-xl">Coach With The Bullpen</span>
              </div>
              <p className="text-sm text-zinc-400 mb-5">Apply to run drill sessions at your own facility through the Pickle Bullpen system. We'll review your application and reach out to get you set up.</p>
              <CoachSignupForm compact />
            </div>
          ) : (
          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <Field label="Full Name" testid="auth-name" value={form.name}
                onChange={(v) => setForm({ ...form, name: v })} placeholder="Your name" required />
            )}
            <Field label="Email" testid="auth-email" type="email" value={form.email}
              onChange={(v) => setForm({ ...form, email: v })} placeholder="you@email.com" required />
            <Field label="Password" testid="auth-password" type="password" value={form.password}
              onChange={(v) => setForm({ ...form, password: v })} placeholder="••••••••" required />
            {mode === "login" && (
              <button type="button" data-testid="forgot-password-link"
                onClick={async () => {
                  if (!form.email.trim()) return toast.error("Enter your email above first, then tap Forgot password");
                  try {
                    const { data } = await api.post("/auth/forgot-password", { email: form.email.trim(), origin_url: window.location.origin });
                    toast.success(data.message);
                  } catch { toast.error("Couldn't send the reset email — try again in a minute"); }
                }}
                className="block text-[11px] text-zinc-500 hover:text-pb-red transition-colors -mt-2">
                Forgot password? <span className="underline">Email me a reset link</span>
              </button>
            )}
            {mode === "register" && (
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">
                  Already Rated? <span className="text-zinc-600 normal-case tracking-normal">{manualDupr ? "DUPR / VAIR (optional)" : "VAIR (optional) · DUPR connects in your Profile"}</span>
                </label>
                <div className={`grid ${manualDupr ? "grid-cols-2" : "grid-cols-1"} gap-2`}>
                  {manualDupr && <input data-testid="auth-dupr" type="number" step="0.001" min="2" max="8" value={extRatings.dupr}
                    onChange={(e) => setExtRatings({ ...extRatings, dupr: e.target.value })} placeholder="DUPR (2.0–8.0)"
                    className="w-full bg-pb-elevated border border-pb-border px-3 py-2.5 text-white placeholder-zinc-600 focus:outline-none focus:border-pb-red text-sm" />}
                  <input data-testid="auth-vair" type="number" step="0.001" min="2" max="8" value={extRatings.vair}
                    onChange={(e) => setExtRatings({ ...extRatings, vair: e.target.value })} placeholder="VAIR (2.0–8.0)"
                    className="w-full bg-pb-elevated border border-pb-border px-3 py-2.5 text-white placeholder-zinc-600 focus:outline-none focus:border-pb-red text-sm" />
                </div>
              </div>
            )}
            {mode === "register" && (
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">
                  Referral Code <span className="text-zinc-600 normal-case tracking-normal">(optional)</span>
                </label>
                <input data-testid="auth-referral-code" value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value.toUpperCase())} placeholder="PB-XXXX"
                  className="w-full bg-pb-elevated border border-pb-border px-3 py-2.5 text-white placeholder-zinc-600 focus:outline-none focus:border-pb-cyan text-sm font-mono" />
              </div>
            )}
            {mode === "register" && (
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">Choose Your Pass</label>
                <div className="grid gap-2 grid-cols-2 sm:grid-cols-3">
                  {tiers.map((t) => (
                    <button key={t.id} type="button" data-testid={`plan-${t.id}`} onClick={() => setPlan(t.id)}
                      className={`text-left border p-3 transition-all ${plan === t.id ? (t.access === "vip" ? "border-pb-cyan bg-pb-cyan/10" : "border-pb-red bg-pb-red/10") : "border-pb-border hover:border-zinc-600"}`}>
                      <div className="font-display font-black uppercase text-lg flex items-center gap-1">
                        <TierIcon icon={t.icon} className="w-4 h-4 text-pb-red" />
                        {t.name}
                      </div>
                      <div className="text-[11px] text-zinc-400 leading-tight mt-1 line-clamp-3">
                        {t.price > 0 ? `$${t.price}/mo — ` : "Free — "}{(t.perks || []).slice(0, 2).join(" · ") || "Bullpen membership"}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <button type="submit" data-testid="auth-submit" disabled={busy}
              className="w-full bg-pb-red text-black font-bold uppercase tracking-widest py-3 hover:bg-[#BEF264] active:scale-[0.98] transition-all disabled:opacity-50">
              {busy ? "…" : mode === "login" ? "Enter The Bullpen"
                : selectedTier && selectedTier.price > 0 ? `Join ${selectedTier.name} · $${selectedTier.price}/mo` : "Create Free Account"}
            </button>
            <p className="text-[10px] text-zinc-500 text-center leading-relaxed" data-testid="auth-consent">By continuing you agree to our <Link to="/terms-of-service" className="text-zinc-300 underline hover:text-pb-red">Terms of Service</Link> and <Link to="/privacy-policy" className="text-zinc-300 underline hover:text-pb-red">Privacy Policy</Link>, and to receive transactional texts/emails about your account and events.</p>
            <div className="flex items-center gap-3 my-1">
              <div className="flex-1 h-px bg-pb-border" />
              <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-600">or</span>
              <div className="flex-1 h-px bg-pb-border" />
            </div>
            <button type="button" data-testid="google-signin-btn"
              onClick={() => {
                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                const redirectUrl = window.location.origin + "/profile";
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              className="w-full flex items-center justify-center gap-3 bg-white text-black font-bold text-sm py-3 hover:bg-zinc-200 active:scale-[0.98] transition-all">
              <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.2 6.1 29.4 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.2 6.1 29.4 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C40.9 35.6 44 30.3 44 24c0-1.3-.1-2.6-.4-3.9z"/></svg>
              Continue with Google
            </button>
          </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, required, testid }) {
  return (
    <div>
      <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">{label}</label>
      <input data-testid={testid} type={type} value={value} required={required} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-pb-elevated border border-pb-border px-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-pb-red focus:ring-1 focus:ring-pb-red transition-colors" />
    </div>
  );
}
